// src/components/services/ServiceForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createService,
  updateService,
  fetchService,
  clearServiceError,
  clearServiceSuccess,
} from '../../store/actions/serviceActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const ServiceForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedService, loading, error, success } = useSelector(
    (state) => state.services
  );

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!id;

  // Icons suggestions for services
  const iconSuggestions = [
    '🔧', '🛠️', '🔨', '⚙️', '🔩', '🔋', '💡', '🔌', '💻', '📱',
    '🚗', '🚚', '🏍️', '🚲', '🏠', '🏢', '🏥', '🏪', '🏫', '🏦',
    '💳', '💰', '📊', '📈', '📉', '🔒', '🔓', '🔑', '📝', '📋',
    '👨‍🔧', '👩‍🔧', '👨‍💼', '👩‍💼', '👨‍🏫', '👩‍🏫', '👨‍⚕️', '👩‍⚕️', '👨‍🍳', '👩‍🍳'
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    section: {
      marginBottom: '2rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid #e5e7eb',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '120px',
      resize: 'vertical',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    iconGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    iconButton: {
      padding: '0.75rem',
      fontSize: '1.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    iconButtonHover: {
      borderColor: '#2563eb',
      transform: 'scale(1.05)',
    },
    iconButtonSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#dbeafe',
      boxShadow: '0 0 0 2px #2563eb',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchService(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (isEditMode && selectedService) {
      setFormData({
        name: selectedService.name || '',
        description: selectedService.description || '',
        icon: selectedService.icon || '',
        isActive: selectedService.isActive ?? true,
      });
    }
  }, [selectedService, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearServiceError());
      dispatch(clearServiceSuccess());
    };
  }, [dispatch]);

  // Memoized validation function
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du service est requis';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Le nom doit contenir au moins 3 caractères';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Le nom ne peut pas dépasser 100 caractères';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'La description ne peut pas dépasser 500 caractères';
    }

    return newErrors;
  }, [formData.name, formData.description]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  }, []);

  const handleIconSelect = useCallback((icon) => {
    setFormData((prev) => ({
      ...prev,
      icon: icon,
    }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await dispatch(updateService(id, formData));
      } else {
        await dispatch(createService(formData));
      }
      navigate('/admin/services');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm]);

  const handleCancel = useCallback(() => {
    if (formData.name || formData.description || formData.icon) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/services');
    }
  }, [formData, navigate]);

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? 'Modifier le service' : 'Créer un nouveau service'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearServiceError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearServiceSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Informations du service</h2>
          
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="name">
              Nom du service *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Réparation automobile, Entretien, etc."
              style={{
                ...styles.input,
                ...(errors.name ? styles.inputError : {}),
              }}
              onFocus={(e) => {
                e.target.style.borderColor = styles.inputFocus.borderColor;
                e.target.style.boxShadow = styles.inputFocus.boxShadow;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = styles.input.borderColor;
                e.target.style.boxShadow = 'none';
              }}
            />
            {errors.name && (
              <div style={styles.errorText}>{errors.name}</div>
            )}
            <div style={styles.helperText}>
              Minimum 3 caractères, maximum 100 caractères
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Décrivez ce service en quelques phrases..."
              style={{
                ...styles.textarea,
                ...(errors.description ? styles.inputError : {}),
              }}
            />
            {errors.description && (
              <div style={styles.errorText}>{errors.description}</div>
            )}
            <div style={styles.helperText}>
              Maximum 500 caractères
            </div>
          </div>
        </div>

        {/* Icon Selection */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Icône</h2>
          
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="icon">
              Icône personnalisée
            </label>
            <input
              type="text"
              id="icon"
              name="icon"
              value={formData.icon}
              onChange={handleChange}
              placeholder="Ex: 🔧, 🚗, 💻 (un seul emoji)"
              maxLength="2"
              style={styles.input}
            />
            <div style={styles.helperText}>
              Vous pouvez saisir un emoji ou en choisir un ci-dessous
            </div>
          </div>

          <div style={styles.iconGrid}>
            {iconSuggestions.map((icon) => (
              <button
                key={icon}
                type="button"
                onClick={() => handleIconSelect(icon)}
                style={{
                  ...styles.iconButton,
                  ...(formData.icon === icon ? styles.iconButtonSelected : {}),
                }}
                onMouseEnter={(e) => {
                  if (formData.icon !== icon) {
                    e.currentTarget.style.borderColor = styles.iconButtonHover.borderColor;
                    e.currentTarget.style.transform = styles.iconButtonHover.transform;
                  }
                }}
                onMouseLeave={(e) => {
                  if (formData.icon !== icon) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* Status */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Statut</h2>
          
          <div style={styles.fieldGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                style={styles.checkbox}
              />
              Service actif
            </label>
            <div style={styles.helperText}>
              Un service inactif n'apparaîtra pas dans la liste des services disponibles pour les agences
            </div>
          </div>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Modification...' : 'Création...') 
              : (isEditMode ? 'Modifier le service' : 'Créer le service')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/services')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default ServiceForm;